import { FlexBox, H5, Spinner } from '@helloself/hello-self-ui';
import React, { useEffect, useState } from 'react';
import { JSX } from 'react/jsx-runtime';
import Skeleton from 'react-loading-skeleton';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { Heading } from '../../../../../ui';
import { ExpertConnection } from '../../../../connections/model/query/connection';
import { useLazyGetProfilePhotoQuery } from '../../../model/profile';
import useProfileRecordEvent from '../../hooks/useProfileRecordEvent';
import ExpertAvatar from '../../shared/ExpertAvatar';
import Tile from '../../Tile';

export type ConnectionsTileProps = {
    following: ExpertConnection[];
    isLoading: boolean;
    isOwnProfile?: boolean;
};

export function ConnectionsTile({ following, isLoading, isOwnProfile }: ConnectionsTileProps) {
    const [expertAvatars, setExpertAvatars] = useState<JSX.Element[]>([]);
    const [loadingImages, setLoadingImages] = useState(false);
    const [trigger] = useLazyGetProfilePhotoQuery();
    const { recordProfileEvent } = useProfileRecordEvent();

    useEffect(() => {
        const fetchConnectionsProfilePhoto = async () => {
            setLoadingImages(true);
            const firstFour = following.slice(0, 4);
            const experts = [];
            for (let i = 0; i < firstFour.length; i++) {
                let profilePhoto = '';
                try {
                    const presignedGetUrl = await trigger(firstFour[i].userId).unwrap();
                    profilePhoto = presignedGetUrl;
                } catch (error) {
                    // TODO: nothing to do here as image is not found
                }
                experts.push(
                    <StyledAvatar
                        key={firstFour[i].userId}
                        expert={{ firstName: '', profilePhoto }}
                        alt="connection-avatar"
                    />
                );
            }
            setExpertAvatars(experts);
            setLoadingImages(false);
        };

        if (following.length > 0) {
            fetchConnectionsProfilePhoto();
        }
    }, [following, trigger]);

    if (isLoading) {
        return (
            <Tile>
                <H5 margin={'0 0 24px'}>Connections</H5>
                <FlexBox gap={'8px'} data-testid="ConnectionsTile">
                    <FlexBox>
                        {/* expert avatars */}
                        <Skeleton width="48px" height="48px" borderRadius="24px" style={{ marginRight: '-12px' }} />
                        <Skeleton width="48px" height="48px" borderRadius="24px" style={{ marginRight: '-12px' }} />
                        <Skeleton width="48px" height="48px" borderRadius="24px" style={{ marginRight: '-12px' }} />
                        <Skeleton width="48px" height="48px" borderRadius="24px" />
                    </FlexBox>
                    <FlexBox flexDirection={'column'}>
                        {/* connections count */}
                        <Skeleton width="50px" height="32px" />
                        {/* connections link */}
                        <Skeleton width="110px" height="21px" />
                    </FlexBox>
                </FlexBox>
            </Tile>
        );
    }

    return (
        <Tile>
            <div className="space-y-6">
                <Heading level={5}>Connections</Heading>
                <FlexBox gap={'8px'} data-testid="ConnectionsTile">
                    {loadingImages ? <Spinner height="48px" width="48px" /> : <FlexBox>{expertAvatars}</FlexBox>}
                    <FlexBox flexDirection={'column'}>
                        <strong className="text-lg" data-testid="ConnectionsFollowing">
                            {following.length}
                        </strong>
                        {isOwnProfile ? (
                            <Link
                                to={'/network?tab=myconnections'}
                                onClick={() => recordProfileEvent('Clicked Connections Link')}
                            >
                                Connections
                            </Link>
                        ) : (
                            'connections'
                        )}
                    </FlexBox>
                </FlexBox>
            </div>
        </Tile>
    );
}

const StyledAvatar = styled(ExpertAvatar as any)`
    &:not(:last-child) {
        margin-right: -12px;
    }
`;
